/**
 * `ReadinessRing` — compact ring in the page header that answers the
 * founder's question: "am I agent-ready or not?".
 *
 * 2026-10-02 — the ring now has a loading state. While the primary
 * readiness sources haven't settled, an indeterminate arc rotates
 * smoothly around the track (neutral `C.dim` color — no verdict
 * commitment yet) and the label reads "Checking…". When data lands,
 * the arc settles to the real `passing / counted` sweep in the
 * verdict's color (mint / amber / coral), the counter switches from
 * "—" to "N/M", and the label becomes the three closed verdict
 * strings. `prefers-reduced-motion` swaps the rotation for a gentle
 * opacity pulse on the same arc.
 *
 * The track + arc are 2px strokes on a 64px square; lime stays
 * reserved for the primary button on the page.
 */

import type { ReadinessVerdict, ReadinessVerdictKind } from '@sill/types';

import { useC } from '../../lib/theme.js';
import { monoFont, sansFont, serifFont } from './fonts.js';

/** Semantic palette (CLAUDE.md §4) — mint=confirmed, amber=escalate,
 *  coral=reject. Light theme uses the *Fg variants (darker, meets
 *  AA on a light surface). */
function verdictArcColor(kind: ReadinessVerdictKind, C: ReturnType<typeof useC>): string {
  if (kind === 'agent_ready') return C.mintFg;
  if (kind === 'almost_ready') return C.amberFg;
  return C.coralFg;
}

export interface ReadinessRingProps {
  readonly verdict: ReadinessVerdict;
  readonly loading?: boolean;
  /** True when the site has no completed snapshot. Ring draws a neutral
   *  (dim) ring with "—" and the label reads "Not checked yet". Never
   *  the verdict colour. */
  readonly no_verdict?: boolean;
  /** When provided, the ring + label become a single button that opens
   *  the "What's left" explainer drawer. The button carries the same
   *  aria-label as the group (verdict + fraction). */
  readonly onExplain?: () => void;
}

const SIZE = 64;
const STROKE = 2;
const RADIUS = (SIZE - STROKE) / 2;
const CIRCUM = 2 * Math.PI * RADIUS;
// Length of the moving arc while the ring is in its indeterminate
// state. 28% of the circumference — long enough to read as rotation,
// short enough to look like a loader, not a verdict.
const LOADING_ARC = CIRCUM * 0.28;

const KEYFRAMES_ID = 'sill-readiness-ring-keyframes';

/**
 * Inject the keyframes once on the first client render. Done
 * imperatively (not through styled-components) because the project
 * stays on bespoke CSS-in-JS; adding a CSS pipeline for one animation
 * would be over-reach. Keyframes also respect `prefers-reduced-motion`
 * via `@media` — motion-safe gets the rotation, motion-reduce gets
 * the pulse.
 */
function ensureKeyframes(): void {
  if (typeof document === 'undefined') return;
  if (document.getElementById(KEYFRAMES_ID) !== null) return;
  const style = document.createElement('style');
  style.id = KEYFRAMES_ID;
  style.textContent = `
    @keyframes sill-readiness-ring-spin {
      from { transform: rotate(-90deg); }
      to { transform: rotate(270deg); }
    }
    @keyframes sill-readiness-ring-pulse {
      0% { opacity: 0.35; }
      50% { opacity: 0.9; }
      100% { opacity: 0.35; }
    }
    @media (prefers-reduced-motion: reduce) {
      [data-testid="readiness-ring-loading"] {
        animation: none !important;
      }
      [data-testid="readiness-ring-loading"] circle {
        animation: sill-readiness-ring-pulse 2200ms ease-in-out infinite !important;
      }
    }
  `;
  document.head.appendChild(style);
}

export function ReadinessRing({ verdict, loading = false, no_verdict = false, onExplain }: ReadinessRingProps): JSX.Element {
  const C = useC();
  const { passing, counted, label } = verdict;
  if (loading) ensureKeyframes();
  const pct = counted === 0 ? 0 : passing / counted;
  // Indeterminate state: a fixed-length arc rotates around the ring at
  // a constant speed. The arc stroke is `C.dim` (neutral, non-commital).
  // Determinate state: the arc color comes from the verdict kind.
  // No-verdict state: the ring is a flat dim circle with "—" inside —
  // the site has no completed readiness snapshot, so we must not
  // commit to a colour.
  const aria_label = no_verdict
    ? 'Not checked yet'
    : loading
      ? 'Checking readiness'
      : `${label}: ${counted === 0 ? 'not measured' : `${passing} of ${counted} checks passing`}`;

  const Wrapper = onExplain !== undefined
    ? ({ children }: { children: React.ReactNode }) => (
        <button
          type="button"
          onClick={onExplain}
          aria-label={`${aria_label}. See what's left.`}
          data-testid="readiness-ring-explain"
          style={{
            background: 'transparent',
            border: 'none',
            padding: 0,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 14,
            fontFamily: sansFont,
            textAlign: 'left',
            color: 'inherit',
          }}
        >
          {children}
        </button>
      )
    : ({ children }: { children: React.ReactNode }) => (
        <div
          role="group"
          aria-label={aria_label}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 14,
            fontFamily: sansFont,
          }}
        >
          {children}
        </div>
      );

  return (
    <Wrapper>
      <div style={{ position: 'relative', width: SIZE, height: SIZE, flexShrink: 0 }}>
        <svg width={SIZE} height={SIZE} viewBox={`0 0 ${SIZE} ${SIZE}`} aria-hidden="true">
          {/* Full backing ring. */}
          <circle
            cx={SIZE / 2}
            cy={SIZE / 2}
            r={RADIUS}
            fill="none"
            stroke={C.borderSoft}
            strokeWidth={STROKE}
          />
          {loading ? (
            <g
              data-testid="readiness-ring-loading"
              style={{
                transformOrigin: `${SIZE / 2}px ${SIZE / 2}px`,
                animation: 'sill-readiness-ring-spin 1200ms linear infinite',
              }}
            >
              <circle
                cx={SIZE / 2}
                cy={SIZE / 2}
                r={RADIUS}
                fill="none"
                stroke={C.dim}
                strokeWidth={STROKE}
                strokeDasharray={`${LOADING_ARC} ${CIRCUM}`}
                strokeLinecap="butt"
                style={{
                  animation: 'sill-readiness-ring-pulse 1600ms ease-in-out infinite',
                }}
              />
            </g>
          ) : no_verdict ? null : (
            <circle
              cx={SIZE / 2}
              cy={SIZE / 2}
              r={RADIUS}
              fill="none"
              stroke={verdictArcColor(verdict.kind, C)}
              strokeWidth={STROKE}
              strokeDasharray={`${CIRCUM * pct} ${CIRCUM}`}
              strokeLinecap="butt"
              transform={`rotate(-90 ${SIZE / 2} ${SIZE / 2})`}
              style={{ transition: 'stroke-dasharray 300ms ease, stroke 200ms ease' }}
            />
          )}
        </svg>
        <span
          aria-hidden="true"
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontFamily: monoFont,
            fontSize: 11,
            color: C.body,
            letterSpacing: '0.04em',
          }}
        >
          {no_verdict || loading || counted === 0 ? '—' : `${passing}/${counted}`}
        </span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
        <span
          style={{
            fontFamily: serifFont,
            fontSize: 18,
            color: C.text,
            lineHeight: 1.1,
          }}
        >
          {no_verdict ? 'Not checked yet' : loading ? 'Checking…' : label}
        </span>
        <span style={{ fontFamily: monoFont, fontSize: 10, color: C.muted, letterSpacing: '0.06em' }}>
          {no_verdict ? 'RUN YOUR FIRST CHECK' : loading ? 'LOADING CHECKS' : counted === 0
            ? 'NOT MEASURED YET'
            : `${passing} OF ${counted} CHECKS PASSING`}
        </span>
      </div>
    </Wrapper>
  );
}

/** Small label the header renders below the ring + hero copy. */
export function ReadinessRingFootnote(): JSX.Element {
  const C = useC();
  return (
    <div
      style={{
        fontFamily: monoFont,
        fontSize: 11,
        color: C.muted,
        letterSpacing: '0.04em',
      }}
    >
      Counts the checks below. Emerging standards count less.
    </div>
  );
}
